import { AlertTriangle, RefreshCw, Home, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ErrorPageProps {
  /** Error type determines the icon and message */
  type?: "fatal" | "network" | "server";
  /** Custom error message */
  message?: string;
  /** Callback for retry button */
  onRetry?: () => void;
  /** Whether to show the home button */
  showHomeButton?: boolean;
}

/**
 * Generic error page for various error scenarios.
 * - fatal: Unrecoverable error (e.g., ErrorBoundary caught exception)
 * - network: Connection issue / offline
 * - server: 500 error from API
 */
export function ErrorPage({
  type = "fatal",
  message,
  onRetry,
  showHomeButton = true,
}: ErrorPageProps) {
  const config = {
    fatal: {
      icon: <AlertTriangle className="h-16 w-16 text-destructive" />,
      title: "Something Went Wrong",
      defaultMessage: "An unexpected error occurred. Please try again or contact support.",
    },
    network: {
      icon: <WifiOff className="h-16 w-16 text-orange-500" />,
      title: "Connection Lost",
      defaultMessage: "Please check your internet connection and try again.",
    },
    server: {
      icon: <AlertTriangle className="h-16 w-16 text-destructive" />,
      title: "Server Error",
      defaultMessage: "We're experiencing technical difficulties. Please try again later.",
    },
  };

  const { icon, title, defaultMessage } = config[type];

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      // Default: reload the page
      window.location.reload();
    }
  };

  const handleGoHome = () => {
    window.location.href = "/";
  };

  return (
    <div
      className="min-h-screen bg-background flex items-center justify-center px-4"
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
    >
      <div className="text-center max-w-md">
        <div className="flex justify-center mb-6" aria-hidden="true">
          {icon}
        </div>
        <h1 id="error-title" className="text-2xl font-bold text-foreground mb-2">
          {title}
        </h1>
        <p id="error-description" className="text-muted-foreground mb-8">
          {message || defaultMessage}
        </p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            onClick={handleRetry}
            className="flex-1 sm:flex-none"
            aria-describedby="error-description"
          >
            <RefreshCw className="h-4 w-4 mr-2" aria-hidden="true" />
            Try Again
          </Button>

          {showHomeButton && (
            <Button
              variant="outline"
              onClick={handleGoHome}
              className="flex-1 sm:flex-none"
              aria-label="Return to home page"
            >
              <Home className="h-4 w-4 mr-2" aria-hidden="true" />
              Go Home
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ErrorPage;
